import { DriveMode, DriveModeProfile, ShiftState, TransmissionMode, TransmissionProfile } from '../types';
import { BMW_M340I_ZF8HP_PROFILE, TransmissionConfig } from '../config/transmissions';

export type ShiftDirection = 'up' | 'down';

export interface ShiftEvent {
  direction: ShiftDirection;
  fromGear: number;
  toGear: number;
  timestamp: number;
  mode: DriveMode;
  durationMs: number;
}

export class Gearbox {
  private profile: TransmissionProfile;
  private currentGear: number = 1;
  private transmissionMode: TransmissionMode = 'manual';
  private driveMode: DriveMode = 'COMFORT';
  private shiftState: ShiftState = 'IDLE';
  private shiftTimerMs: number = 0;
  private isShifting: boolean = false;
  private lastShiftTime: number = -99999;
  private lastDownshiftTime: number = -99999;
  private isDemandDownshiftLatched: boolean = false;
  private onShiftListeners: Array<(event: ShiftEvent) => void> = [];

  constructor(
    config: Partial<TransmissionConfig> = {},
    initialTransmissionMode: TransmissionMode = 'manual',
    initialDriveMode: DriveMode = 'COMFORT'
  ) {
    this.profile = config.profile || BMW_M340I_ZF8HP_PROFILE;
    this.transmissionMode = initialTransmissionMode;
    this.driveMode = initialDriveMode;
  }

  public getGear(): number {
    return this.currentGear;
  }

  public getMode(): TransmissionMode {
    return this.transmissionMode;
  }

  public setMode(mode: TransmissionMode): void {
    this.transmissionMode = mode;
  }

  public getDriveMode(): DriveMode {
    return this.driveMode;
  }

  public setDriveMode(mode: DriveMode): void {
    this.driveMode = mode;
  }

  public getProfile(): TransmissionProfile {
    return this.profile;
  }

  public setProfile(profile: TransmissionProfile): void {
    this.profile = profile;
    if (this.currentGear > this.profile.gearRatios.length) {
      this.currentGear = this.profile.gearRatios.length;
    }
  }

  public getDriveModeProfile(): DriveModeProfile {
    switch (this.driveMode) {
      case 'ECO':
        return this.profile.eco;
      case 'SPORT':
        return this.profile.sport;
      case 'COMFORT':
      default:
        return this.profile.comfort;
    }
  }

  public getGearCount(): number {
    return this.profile.gearRatios.length;
  }

  public getRatio(gear: number = this.currentGear): number {
    const idx = Math.max(1, Math.min(this.profile.gearRatios.length, gear)) - 1;
    return this.profile.gearRatios[idx] || 1.0;
  }

  public getFinalDriveRatio(): number {
    return this.profile.finalDriveRatio;
  }

  public getWheelCircumference(): number {
    return this.profile.wheelCircumference;
  }

  public getShiftState(): ShiftState {
    return this.shiftState;
  }

  public getShiftTimerMs(): number {
    return this.shiftTimerMs;
  }

  public isShiftInProgress(): boolean {
    return this.isShifting || this.shiftState !== 'IDLE';
  }

  public getPredictedDownshiftRpm(currentRpm: number, gearOffset: number = 1): number | null {
    const targetGear = this.currentGear - gearOffset;
    if (targetGear < 1 || targetGear >= this.currentGear) return null;
    const currentRatio = this.getRatio(this.currentGear);
    const targetRatio = this.getRatio(targetGear);
    return Math.round(currentRpm * (targetRatio / currentRatio));
  }

  public isDownshiftCoolingDown(now: number = performance.now()): boolean {
    const modeProfile = this.getDriveModeProfile();
    const cooldown = modeProfile.downshiftCooldownMs ?? (this.driveMode === 'SPORT' ? 1000 : 1500);
    return (now - this.lastDownshiftTime) < cooldown;
  }

  public isDemandDownshiftActive(): boolean {
    return this.isDemandDownshiftLatched;
  }

  public addShiftListener(listener: (event: ShiftEvent) => void): () => void {
    this.onShiftListeners.push(listener);
    return () => {
      this.onShiftListeners = this.onShiftListeners.filter(l => l !== listener);
    };
  }

  /**
   * Manual Shift Up
   */
  public shiftUp(now: number = performance.now()): boolean {
    if (this.currentGear >= this.getGearCount()) return false;
    return this.executeShift(this.currentGear + 1, 'up', now);
  }

  /**
   * Manual Shift Down
   */
  public shiftDown(now: number = performance.now()): boolean {
    if (this.currentGear <= 1) return false;
    return this.executeShift(this.currentGear - 1, 'down', now);
  }

  /**
   * Set specific gear
   */
  public setGear(gear: number, now: number = performance.now()): boolean {
    const target = Math.max(1, Math.min(this.getGearCount(), gear));
    if (target === this.currentGear) return false;
    const direction: ShiftDirection = target > this.currentGear ? 'up' : 'down';
    return this.executeShift(target, direction, now);
  }

  /**
   * Update internal shift timer tick
   */
  public tick(dtMs: number): void {
    if (this.shiftTimerMs > 0) {
      this.shiftTimerMs = Math.max(0, this.shiftTimerMs - dtMs);
      if (this.shiftTimerMs === 0) {
        this.shiftState = 'IDLE';
        this.isShifting = false;
      }
    }
  }

  /**
   * Evaluate automatic gear change based on RPM, vehicle speed, and throttle effort
   * Natural downshifting control logic:
   * 1. Condition A: Lugging protection (shift down when RPM drops below lugging threshold)
   * 2. Condition B: Acceleration demand (throttle hysteresis enter/exit, only downshift if powerband requires)
   * 3. Condition C: Deceleration / engine braking (orderly downshifts as speed drops)
   * 4. Ceiling protection: Predicted RPM clamp (maxComfortableDownshiftRPM) to avoid jarring high RPM jumps
   * 5. Cooldown timer: Eliminates rapid downshift hunting
   */
  public checkAutoShift(
    currentRpm: number,
    effort: number,
    speedKmh: number,
    now: number = performance.now()
  ): boolean {
    if (this.transmissionMode !== 'automatic') return false;

    const modeProfile = this.getDriveModeProfile();
    const shiftDuration = modeProfile.shiftDuration || 250;

    // Clear shifting state if elapsed
    if (this.isShifting && now - this.lastShiftTime >= shiftDuration) {
      this.isShifting = false;
      this.shiftState = 'IDLE';
      this.shiftTimerMs = 0;
    }

    if (this.isShifting || this.shiftState !== 'IDLE') return false;

    // Minimum inter-shift hysteresis
    const minShiftInterval = this.driveMode === 'SPORT' ? 550 : 850;
    if (now - this.lastShiftTime < minShiftInterval) {
      return false;
    }

    // Stationary reset: snap to 1st gear if car comes to a stop
    if (speedKmh < 2.0 && this.currentGear > 1) {
      return this.executeShift(1, 'down', now);
    }

    // Throttle / load normalization
    const throttle = Math.max(0.0, Math.min(1.0, effort));

    // Update throttle demand hysteresis latch
    const enterDemand = modeProfile.downshiftThrottleEnter ?? (this.driveMode === 'SPORT' ? 0.60 : 0.65);
    const exitDemand = modeProfile.downshiftThrottleExit ?? (this.driveMode === 'SPORT' ? 0.40 : 0.45);

    if (throttle >= enterDemand) {
      this.isDemandDownshiftLatched = true;
    } else if (throttle < exitDemand) {
      this.isDemandDownshiftLatched = false;
    }

    // Dynamic upshift threshold interpolation
    let upshiftRpm: number;
    if (throttle <= 0.5) {
      const k = throttle / 0.5;
      upshiftRpm =
        modeProfile.lightThrottleUpshiftRPM +
        (modeProfile.mediumThrottleUpshiftRPM - modeProfile.lightThrottleUpshiftRPM) * k;
    } else {
      const k = (throttle - 0.5) / 0.5;
      upshiftRpm =
        modeProfile.mediumThrottleUpshiftRPM +
        (modeProfile.hardThrottleUpshiftRPM - modeProfile.mediumThrottleUpshiftRPM) * k;
    }

    // Gear holding logic in SPORT mode: hold lower gear when active on throttle or braking
    if (this.driveMode === 'SPORT' && (throttle > 0.35 || effort < -0.2)) {
      upshiftRpm += 500 * modeProfile.gearHoldStrength;
    }

    const currentRatio = this.getRatio(this.currentGear);

    // -------------------------------------------------------------
    // 1. CHECK DOWNSHIFT
    // -------------------------------------------------------------
    const downshiftCooldown = modeProfile.downshiftCooldownMs ?? (this.driveMode === 'SPORT' ? 1000 : 1500);
    const isCooldownActive = (now - this.lastDownshiftTime) < downshiftCooldown;

    if (this.currentGear > 1 && speedKmh > 3.0 && !isCooldownActive) {
      // Condition A: Low-RPM Lugging Protection
      const luggingRpm = modeProfile.lightThrottleDownshiftRPM;
      const isLugging = currentRpm <= luggingRpm;

      // Condition B: Throttle / Powerband Demand
      // Only downshift if throttle demands it (latched) AND current RPM is below the powerband target
      let demandDownshiftTargetRpm = modeProfile.mediumThrottleDownshiftRPM;
      if (this.isDemandDownshiftLatched) {
        const enter = modeProfile.downshiftThrottleEnter ?? (this.driveMode === 'SPORT' ? 0.60 : 0.65);
        const k = Math.min(1.0, Math.max(0.0, (throttle - enter) / (0.85 - enter)));
        demandDownshiftTargetRpm =
          modeProfile.mediumThrottleDownshiftRPM +
          (modeProfile.hardThrottleDownshiftRPM - modeProfile.mediumThrottleDownshiftRPM) * (0.60 + 0.40 * k);
      }
      const isPowerDemand = this.isDemandDownshiftLatched && currentRpm <= demandDownshiftTargetRpm;

      // Condition C: Deceleration / Engine Braking
      const isDecelBraking = effort < -0.15 && currentRpm <= (modeProfile.mediumThrottleDownshiftRPM * 1.05);

      // Kickdown: Throttle aggressively slammed
      const isKickdown = throttle > 0.82 && currentRpm < (upshiftRpm * 0.85);

      if (isLugging || isPowerDemand || isDecelBraking || isKickdown) {
        // Determine target gear (Max 1 gear normally, max 2 gears on aggressive kickdown)
        let targetGear = this.currentGear - 1;

        if (isKickdown && this.currentGear > 2) {
          const twoGearsDown = this.currentGear - 2;
          const twoGearsRatio = this.getRatio(twoGearsDown);
          const predictedTwoGearRpm = currentRpm * (twoGearsRatio / currentRatio);
          const maxCeiling = modeProfile.maxComfortableDownshiftRPM ?? (this.driveMode === 'SPORT' ? 4500 : 3200);

          // Allow 2 gears downshift only if target gear lands within comfortable ceiling and safe powerband
          if (predictedTwoGearRpm <= maxCeiling && predictedTwoGearRpm < modeProfile.hardThrottleUpshiftRPM * 0.90) {
            targetGear = twoGearsDown;
          }
        }

        // Predicted RPM & Ceiling Protection Check
        const targetRatio = this.getRatio(targetGear);
        const predictedRpm = currentRpm * (targetRatio / currentRatio);
        const maxComfortableRpm = modeProfile.maxComfortableDownshiftRPM ?? (this.driveMode === 'SPORT' ? 4500 : 3200);

        // Allow downshift if:
        // - predicted RPM does not exceed comfortable ceiling, OR
        // - throttle is high (> 70% demand override), OR
        // - hard braking (effort < -0.5)
        const isCeilingAllowed = predictedRpm <= maxComfortableRpm || throttle > 0.70 || effort < -0.50;

        // Mechanical rev limiter ceiling (safety against over-revving engine)
        const absoluteSafeMaxRpm = this.driveMode === 'SPORT' ? 8500 : 6600;
        const isMechanicallySafe = predictedRpm < absoluteSafeMaxRpm;

        if (isCeilingAllowed && isMechanicallySafe) {
          return this.executeShift(targetGear, 'down', now);
        }

        // Downshift was requested but blocked by ceiling or rev limiter:
        // Suppress upshift and hold the current gear stably!
        return false;
      }
    }

    // -------------------------------------------------------------
    // 2. CHECK UPSHIFT
    // -------------------------------------------------------------
    if (this.currentGear < this.getGearCount()) {
      const nextGear = this.currentGear + 1;
      const nextGearRatio = this.getRatio(nextGear);
      const expectedNextRpm = currentRpm * (nextGearRatio / currentRatio);

      // Ensure that shifting up won't immediately trigger an anti-hunting downshift
      const minSafeRpm = this.currentGear === 1 
        ? (modeProfile.lightThrottleDownshiftRPM * 0.95) 
        : (modeProfile.lightThrottleDownshiftRPM * 1.05);

      const isAntiHuntingSafe = expectedNextRpm >= minSafeRpm;

      if (currentRpm >= upshiftRpm && isAntiHuntingSafe) {
        return this.executeShift(nextGear, 'up', now);
      }
    }

    return false;
  }

  private executeShift(
    toGear: number,
    direction: ShiftDirection,
    now: number = performance.now()
  ): boolean {
    const fromGear = this.currentGear;
    this.currentGear = toGear;
    this.lastShiftTime = now;
    if (direction === 'down') {
      this.lastDownshiftTime = now;
    }
    this.isShifting = true;
    this.shiftState = direction === 'up' ? 'UPSHIFT' : 'DOWNSHIFT';

    const modeProfile = this.getDriveModeProfile();
    const duration = modeProfile.shiftDuration || 250;
    this.shiftTimerMs = duration;

    const event: ShiftEvent = {
      direction,
      fromGear,
      toGear,
      timestamp: now,
      mode: this.driveMode,
      durationMs: duration,
    };

    // Notify listeners for sound FX (e.g. rev blip, shift pop)
    this.onShiftListeners.forEach(listener => listener(event));

    // Reset shift transient after duration
    setTimeout(() => {
      this.shiftTimerMs = 0;
      this.shiftState = 'IDLE';
      this.isShifting = false;
    }, duration);

    return true;
  }

  public reset(gear: number = 1): void {
    this.currentGear = gear;
    this.shiftState = 'IDLE';
    this.isShifting = false;
    this.shiftTimerMs = 0;
    this.lastShiftTime = -99999;
    this.lastDownshiftTime = -99999;
    this.isDemandDownshiftLatched = false;
  }
}
